import { useTranslation } from "react-i18next";

import { resolveAssetUrl } from "@/lib/api/http";
import { field } from "@/lib/api/normalize";


/** Latin digits are kept in Arabic so prices stay legible next to the $ mark. */
function localeFor(lang?: string) {
  return lang?.startsWith("ar") ? "ar-EG-u-nu-latn" : "en-US";
}

export function formatPrice(value: number | string | null | undefined, currency = "EGP ", lang?: string) {
  // Tolerates the legacy `formatPrice(value, lang)` call shape used across the
  // admin pages, which otherwise rendered the language code as the currency.
  if (currency === "en" || currency === "ar" || currency?.startsWith("ar-") || currency?.startsWith("en-")) {
    lang = currency;
    currency = "EGP ";
  }
  const amount = Number(value);
  const safe = Number.isFinite(amount) ? amount : 0;
  return `${currency.trim()}\u00A0${safe.toLocaleString(localeFor(lang), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}


export function formatNumber(value: number, lang?: string) {
  return value.toLocaleString(localeFor(lang));
}

/** Locale-aware price/number formatters bound to the active language. */
export function useFormatters() {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  return {
    lang,
    formatPrice: (value: number, currency = "EGP ") => formatPrice(value, currency, lang),
    formatNumber: (value: number) => formatNumber(value, lang),
  };
}

/** Reads a localized field, tolerating camelCase and snake_case payloads. */
export function localized(obj: any, field: string, lang: string) {
  if (!obj) return '';
  const camel = (s: string) => s.replace(/[_-]+([a-zA-Z0-9])/g, (_m, c: string) => c.toUpperCase());
  const pick = (suffix: 'ar' | 'en') => {
    const snake = `${field}_${suffix}`;
    return obj[snake] ?? obj[camel(snake)] ?? '';
  };
  if (lang === 'ar' && pick('ar')) return pick('ar');
  return pick('en') || pick('ar') || obj[field] || '';
}

export function formatDeliveryDays(min: number, max: number, lang?: string) {
  const lo = Math.max(1, Number(min) || 1);
  const hi = Math.max(lo, Number(max) || lo);
  if (lang === 'ar') {
    if (lo === hi) return lo === 1 ? 'يوم عمل واحد' : lo === 2 ? 'يومان عمل' : `${lo} ${lo <= 10 ? 'أيام' : 'يوم'} عمل`;
    return `من ${lo} إلى ${hi} أيام عمل`;
  }
  if (lo === hi) return `${lo} Business Day${lo === 1 ? '' : 's'}`;
  return `${lo}-${hi} Business Days`;
}


/** Resolves a stored asset path/URL onto the configured backend origin. */
export function imgUrl(url: unknown, _size?: string) {
  return resolveAssetUrl(url);
}


/**
 * Picks the first usable image on an API row, tolerating the different shapes
 * the backend returns (`image`, `imageUrl`, `mainImageUrl`, `images[]`, …).
 */
export function rowImg(row: any): string {
  const candidate =
    field(row, "main_image_url", "image_url", "image", "thumbnail", "cover_image") ??
    (Array.isArray(field(row, "images")) ? (field<any[]>(row, "images") as any[])[0] : undefined);
  return resolveAssetUrl(candidate);
}
